/**
 * Anonymous Identity Management
 * 
 * Manages the clientId - a stable identifier for anonymous users.
 * Stored in both localStorage and cookie for redundancy.
 * 
 * See: docs/ANONYMOUS-PROFILE-DESIGN.md
 */

const CLIENT_ID_KEY = 'anon_client_id';

/**
 * Validate that a string is a valid UUID v4
 */
function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Generate a new UUID v4
 */
function generateUUID(): string {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Set clientId cookie (first-party, long duration)
 */
function setClientIdCookie(clientId: string): void {
  const maxAge = 365 * 24 * 60 * 60; // 1 year in seconds
  document.cookie = `${CLIENT_ID_KEY}=${clientId}; max-age=${maxAge}; path=/; SameSite=Lax`;
}

/**
 * Read clientId from cookie
 */
function getClientIdFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CLIENT_ID_KEY && value) {
      return value;
    }
  }
  return null;
}

/**
 * Get or create a stable clientId for the anonymous user.
 * Tries multiple sources in order of priority:
 * 1. localStorage
 * 2. cookie (backup if localStorage was cleared)
 * 3. Generate new
 */
export function getOrCreateClientId(): string {
  // 1. Try from localStorage
  let clientId = localStorage.getItem(CLIENT_ID_KEY);
  
  if (clientId && isValidUUID(clientId)) {
    // Ensure it's also in cookie for redundancy
    setClientIdCookie(clientId);
    return clientId;
  }
  
  // 2. Try from cookie (backup if localStorage was cleared)
  clientId = getClientIdFromCookie();
  
  if (clientId && isValidUUID(clientId)) {
    // Restore to localStorage
    localStorage.setItem(CLIENT_ID_KEY, clientId);
    return clientId;
  }
  
  // 3. Generate new
  clientId = generateUUID();
  
  // Store in both places for redundancy
  localStorage.setItem(CLIENT_ID_KEY, clientId);
  setClientIdCookie(clientId);
  
  return clientId;
}

/**
 * Get the current clientId without creating a new one
 */
export function getCurrentClientId(): string | null {
  const fromStorage = localStorage.getItem(CLIENT_ID_KEY);
  if (fromStorage && isValidUUID(fromStorage)) {
    return fromStorage;
  }
  
  const fromCookie = getClientIdFromCookie();
  if (fromCookie && isValidUUID(fromCookie)) {
    return fromCookie;
  }
  
  return null;
}

/**
 * Restore clientId from backend (when server identifies user by another means)
 * Updates both localStorage and cookie
 */
export function restoreClientId(clientId: string): void {
  if (!isValidUUID(clientId)) {
    console.warn('Invalid clientId received from server');
    return;
  }
  
  localStorage.setItem(CLIENT_ID_KEY, clientId);
  setClientIdCookie(clientId);
}

/**
 * Clear the clientId (for logout or testing)
 */
export function clearClientId(): void {
  localStorage.removeItem(CLIENT_ID_KEY);
  // Clear cookie by setting expired
  document.cookie = `${CLIENT_ID_KEY}=; max-age=0; path=/`;
}

// ========== LAST KNOWN USER (G4) ==========
// These functions help identify known users even when their session has expired

const LAST_USER_KEY = 'last_known_user_id';

/**
 * Save the user's ID when they log in
 * This persists even after logout for identification purposes
 */
export function setLastKnownUserId(userId: string): void {
  localStorage.setItem(LAST_USER_KEY, userId);
}

/**
 * Get the last known user ID (if any)
 * Returns null if user has never logged in on this device
 */
export function getLastKnownUserId(): string | null {
  return localStorage.getItem(LAST_USER_KEY);
}

/**
 * Clear the last known user ID
 * Only call this if user explicitly wants to be forgotten
 */
export function clearLastKnownUserId(): void {
  localStorage.removeItem(LAST_USER_KEY);
}

// ========== DEVICE FINGERPRINT ==========
// For multi-device tracking

const FINGERPRINT_KEY = 'device_fingerprint';

/**
 * Get the current device fingerprint
 * This is initialized by the tracking module and stored in localStorage
 */
export function getDeviceFingerprint(): string | null {
  return localStorage.getItem(FINGERPRINT_KEY);
}

/**
 * Get or create a device fingerprint
 * Used when we need a fingerprint but tracking hasn't initialized yet
 */
export function getOrCreateDeviceFingerprint(): string {
  const existing = localStorage.getItem(FINGERPRINT_KEY);
  if (existing) {
    return existing;
  }
  
  // Generate a fallback fingerprint
  const fallback = `fp_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  localStorage.setItem(FINGERPRINT_KEY, fallback);
  return fallback;
}

